import { z } from 'zod';
import { TimestampsSchema } from './common.js';
// =============================================================================
// Document Analysis Schema - AI/Textract results (separate from Document)
// =============================================================================
/**
 * DocuSign tab types
 */
export const TabTypeEnum = z.enum([
    'SIGN_HERE',
    'INITIAL_HERE',
    'DATE_SIGNED',
    'FULL_NAME',
    'TEXT',
    'CHECKBOX',
]);
/**
 * Signer roles
 */
export const SignerRoleEnum = z.enum([
    'BUYER_1',
    'BUYER_2',
    'BUYER_3',
    'BUYER_4',
    'SELLER_1',
    'SELLER_2',
    'SELLER_3',
    'SELLER_4',
    'AGENT_BUYER',
    'AGENT_SELLER',
]);
/**
 * Analysis status
 */
export const AnalysisStatusEnum = z.enum(['PENDING', 'PROCESSING', 'SUCCEEDED', 'ERROR']);
/**
 * Analysis engine type
 */
export const AnalysisEngineEnum = z.enum(['textract', 'gemini', 'custom']);
/**
 * Signature candidate - detected signature field location
 * Uses normalized coordinates (0-1 ratio of page dimensions)
 */
export const SignatureCandidateSchema = z.object({
    id: z.string().optional(), // Unique ID for frontend tracking
    page: z.number().int().positive(),
    // Normalized coordinates (0-1 range, ratio of page dimensions)
    xNorm: z.number().min(0).max(1),
    yNorm: z.number().min(0).max(1),
    wNorm: z.number().min(0).max(1),
    hNorm: z.number().min(0).max(1),
    // Tab configuration
    tabType: TabTypeEnum,
    role: SignerRoleEnum.optional(),
    roleHints: z.array(z.string()).max(5).default([]), // Detected text hints
    // Display
    label: z.string().optional(),
    anchorString: z.string().optional(), // e.g., "**sig_here_buyer1**"
    // Confidence score from AI
    confidence: z.number().min(0).max(1).default(0.6),
    // User modifications
    isUserModified: z.boolean().default(false),
    isDeleted: z.boolean().default(false),
});
/**
 * Issue severity levels
 */
export const IssueSeverityEnum = z.enum(['LOW', 'MEDIUM', 'HIGH', 'CRITICAL']);
/**
 * Critical issue detected in document
 * v5.1: Added sourceDocumentId for property-level synthesis
 */
export const CriticalIssueSchema = z.object({
    rank: z.number().int().positive().optional(),
    title: z.string(),
    description: z.string().optional(),
    severity: z.string(), // 'Critical', 'Medium', 'Low', 'High'
    page: z.number().int().positive().optional(),
    imageRef: z.string().optional(), // Reference to supporting image (image_id from VLM analysis)
    recommendedAction: z.string().optional(),
    estimatedCost: z.string().optional(), // e.g., "$8,000 - $15,000"
    source: z.string().optional(), // e.g., "Home Inspection Report (p. 12-13)"
    sourceDocumentId: z.string().optional(), // v5.1: Document ID where this issue was found (for property-level synthesis)
});
/**
 * Location scores for property (from location intelligence pipeline)
 */
export const LocationScoresSchema = z.object({
    schools: z.number().min(0).max(10).optional(),
    safety: z.number().min(0).max(10).optional(),
    neighborhood: z.number().min(0).max(10).optional(),
    investment: z.number().min(0).max(10).optional(),
});
/**
 * Neighborhood information sections (v3.6.1: added safetyInfo + investmentInfo)
 */
export const NeighborhoodInfoSchema = z.object({
    locationDemographics: z.string().optional(),
    schoolsEducation: z.string().optional(),
    amenitiesTransportation: z.string().optional(),
    safetyInfo: z.string().optional(),
    investmentInfo: z.string().optional(),
});
/**
 * Document insights (AI-generated from Python worker)
 */
export const DocumentInsightsSchema = z.object({
    // Legacy field (kept for backwards compatibility)
    summary: z.string().optional(),
    // New fields from Python worker
    executiveSummary: z.string().optional(),
    criticalIssues: z.array(CriticalIssueSchema).default([]),
    positiveHighlights: z.array(z.string()).default([]),
    // House condition score (0-100)
    houseScore: z.number().min(0).max(100).optional(),
    houseConditionScore: z.number().min(0).max(100).optional(),
    scoreJustification: z.string().optional(),
    // Location intelligence
    locationScores: LocationScoresSchema.optional(),
    neighborhoodSummary: z.string().optional(),
    neighborhoodInfo: NeighborhoodInfoSchema.optional(),
    // Cost totals
    totalImmediateRepairs: z.string().optional(), // e.g., "$13,650 - $28,900"
    nearTermCosts: z.string().optional(), // e.g., "$12,000 - $18,000 (roof replacement)"
});
/**
 * Full DocumentAnalysis schema (database document)
 */
export const DocumentAnalysisSchema = z
    .object({
    _id: z.string(),
    documentId: z.string(), // Unique reference to Document
    organizationId: z.string(), // For RLS filtering
    // Analysis Status
    status: AnalysisStatusEnum.default('PENDING'),
    engine: AnalysisEngineEnum.optional(),
    startedAt: z.coerce.date().optional(),
    completedAt: z.coerce.date().optional(),
    error: z.string().optional(),
    // Signature Candidates (for UI overlay)
    signatureCandidates: z.array(SignatureCandidateSchema).default([]),
    // Insights (optional)
    insights: DocumentInsightsSchema.optional(),
    // Pointer to full heavy analysis in S3
    fullAnalysisS3Key: z.string().optional(),
    // Pointer to annotated PDF with anchor strings
    annotatedPdfS3Key: z.string().optional(),
    // Deduplication fields
    /** SHA-256 hash of the original PDF file for deduplication */
    fileHash: z.string().optional(),
    /** If this analysis was copied from a duplicate, the source document ID */
    sourceDocumentId: z.string().optional(),
})
    .merge(TimestampsSchema);
/**
 * Update analysis input (for saving user edits to candidates)
 */
export const UpdateAnalysisInputSchema = z.object({
    signatureCandidates: z.array(SignatureCandidateSchema).optional(),
});
/**
 * Analysis response (safe for client)
 */
export const DocumentAnalysisResponseSchema = DocumentAnalysisSchema;
//# sourceMappingURL=document-analysis.js.map